import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Loader2, Trash2, Truck, ChefHat, Info } from 'lucide-react';
import { fmtDate, fmtWindow, positionStyle } from '../../../lib/scheduleFormat';

const OPEN = '__open__';

// Kitchen roles must sit inside a booking; a driver is anchored to a pickup
// inside kitchen availability while the rest of the shift runs free.
const ShiftDialog = ({
  open, shift, date, positions, staff, bookings, availability, presetUserId, presetPosition,
  onClose, onSubmit, onDelete, busy,
}) => {
  const [positionCode, setPositionCode] = useState('baker');
  const [userId, setUserId] = useState(OPEN);
  const [bookingId, setBookingId] = useState('');
  const [dateStr, setDateStr] = useState('');
  const [startTime, setStartTime] = useState('05:00');
  const [endTime, setEndTime] = useState('11:00');
  const [pickupTime, setPickupTime] = useState('06:00');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    const initialPosition = shift?.position_code || presetPosition || positions?.[0]?.code || 'baker';
    setPositionCode(initialPosition);
    setUserId(shift?.user_id || presetUserId || OPEN);
    setDateStr(shift?.date || date || '');
    setNotes(shift?.notes || '');
    setDeliveryDate(shift?.delivery_date || shift?.date || date || '');
    setPickupTime(shift?.pickup_time || '06:00');

    const firstBooking = (bookings || [])[0];
    setBookingId(shift?.booking_id || firstBooking?.id || '');
    setStartTime(shift?.start_time || firstBooking?.start_time || '05:00');
    setEndTime(shift?.end_time || firstBooking?.end_time || '11:00');
  }, [open, shift, date, positions, staff, bookings, presetUserId, presetPosition]);

  const position = useMemo(
    () => (positions || []).find((p) => p.code === positionCode),
    [positions, positionCode]
  );
  const kitchenBound = position ? position.kitchen_bound : true;

  const eligibleStaff = useMemo(
    () => (staff || []).filter((person) => person.active && person.positions.includes(positionCode)),
    [staff, positionCode]
  );

  const activeBooking = useMemo(
    () => (bookings || []).find((b) => b.id === bookingId),
    [bookings, bookingId]
  );

  const payload = () => ({
    date: dateStr,
    position_code: positionCode,
    user_id: userId === OPEN ? null : userId,
    booking_id: kitchenBound ? bookingId : null,
    start_time: startTime,
    end_time: endTime,
    ...(kitchenBound ? {} : { pickup_time: pickupTime, delivery_date: deliveryDate }),
    notes,
  });

  const noBookings = kitchenBound && (bookings || []).length === 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="shift-dialog">
        <DialogHeader>
          <DialogTitle>{shift ? 'Edit shift' : 'Add shift'}</DialogTitle>
          <DialogDescription>{fmtDate(dateStr)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Position</Label>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="shift-positions">
              {(positions || []).map((option) => (
                <button
                  key={option.code}
                  type="button"
                  onClick={() => setPositionCode(option.code)}
                  className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                    positionCode === option.code
                      ? positionStyle(option.code)
                      : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid={`shift-position-${option.code}`}
                >
                  {option.kitchen_bound
                    ? <ChefHat className="mr-1 inline h-3.5 w-3.5" />
                    : <Truck className="mr-1 inline h-3.5 w-3.5" />}
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="shift-person">Who is working</Label>
            <select
              id="shift-person"
              className="mt-1 h-11 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              data-testid="shift-person"
            >
              <option value={OPEN}>Leave open (unassigned)</option>
              {eligibleStaff.map((person) => (
                <option key={person.user_id} value={person.user_id}>{person.name}</option>
              ))}
            </select>
            {eligibleStaff.length === 0 && (
              <p className="mt-1 text-xs text-amber-700" data-testid="shift-no-eligible-staff">
                Nobody is marked as a {position?.label?.toLowerCase() || positionCode} yet — set positions
                on the Kitchen Setup page.
              </p>
            )}
          </div>

          {kitchenBound ? (
            <div>
              <Label htmlFor="shift-booking">Inside which booking</Label>
              {noBookings ? (
                <div className="mt-1 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                  data-testid="shift-no-booking-warning">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    There are no kitchen hours booked on {fmtDate(dateStr)}. Book the kitchen first —
                    a kitchen shift can only sit inside paid hours.
                  </span>
                </div>
              ) : (
                <select
                  id="shift-booking"
                  className="mt-1 h-11 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
                  value={bookingId}
                  onChange={(e) => {
                    setBookingId(e.target.value);
                    const chosen = (bookings || []).find((b) => b.id === e.target.value);
                    if (chosen) { setStartTime(chosen.start_time); setEndTime(chosen.end_time); }
                  }}
                  data-testid="shift-booking"
                >
                  {(bookings || []).map((b) => (
                    <option key={b.id} value={b.id}>
                      {fmtWindow(b.start_time, b.end_time)} · {b.hours}h
                    </option>
                  ))}
                </select>
              )}
            </div>
          ) : (
            <div className="rounded-lg bg-sky-50 p-3 text-xs text-sky-900" data-testid="shift-driver-hint">
              A driver shift is not tied to a booking. The <strong>pickup</strong> must fall while the
              kitchen is open
              {availability?.length > 0 && (
                <> ({availability.map((w) => fmtWindow(w.start_time, w.end_time)).join(', ')})</>
              )}
              ; the rest of the shift is free to run outside it.
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="shift-start">Starts</Label>
              <Input id="shift-start" type="time" value={startTime}
                onChange={(e) => setStartTime(e.target.value)} data-testid="shift-start" />
            </div>
            <div>
              <Label htmlFor="shift-end">Ends</Label>
              <Input id="shift-end" type="time" value={endTime}
                onChange={(e) => setEndTime(e.target.value)} data-testid="shift-end" />
            </div>
          </div>

          {activeBooking && kitchenBound && (
            <p className="text-xs text-gray-500">
              Booking runs {fmtWindow(activeBooking.start_time, activeBooking.end_time)} — the shift must
              sit inside it.
            </p>
          )}

          {!kitchenBound && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="shift-pickup">Pickup at the kitchen</Label>
                <Input id="shift-pickup" type="time" value={pickupTime}
                  onChange={(e) => setPickupTime(e.target.value)} data-testid="shift-pickup" />
              </div>
              <div>
                <Label htmlFor="shift-delivery-date">Delivery date</Label>
                <Input id="shift-delivery-date" type="date" value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)} data-testid="shift-delivery-date" />
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="shift-notes">Notes (optional)</Label>
            <Textarea id="shift-notes" value={notes} rows={2}
              onChange={(e) => setNotes(e.target.value)} data-testid="shift-notes" />
          </div>

          {shift?.status === 'published' && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700"
              data-testid="shift-published-note">
              This shift is already published. Changing the time resets the person&apos;s confirmation and
              asks them to reconfirm.
            </div>
          )}

          {shift?.conflict && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
              data-testid="shift-conflict">
              <Badge variant="outline" className="mr-2 bg-white text-red-700">Conflict</Badge>
              {shift.conflict.message}
            </div>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          {shift ? (
            <Button
              variant="outline" className="text-red-600 hover:bg-red-50"
              onClick={() => onDelete(shift)} disabled={busy}
              data-testid="shift-delete"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Remove shift
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="shift-cancel">Close</Button>
            <Button
              onClick={() => onSubmit(payload())}
              disabled={busy || !dateStr || (kitchenBound && !bookingId)}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="shift-save"
            >
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {shift ? 'Save shift' : 'Add shift'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShiftDialog;
